// DataTrace TIQSA Title Search API Service
// Handles title searches using DataTrace TIQSA API (current_owner_bronze/silver products)

import { getCredentials } from './datatraceApi';

/**
 * Search for title information using DataTrace TIQSA API
 * @param {Object} searchParams - Search parameters
 * @param {string} searchParams.address - Property address
 * @param {string} searchParams.city - City
 * @param {string} searchParams.state - State code
 * @param {string} searchParams.zip - ZIP code
 * @param {string} searchParams.county - County name
 * @param {string} searchParams.apn - Assessor's Parcel Number (optional)
 * @param {string} productType - 'current_owner_bronze' or 'current_owner_silver' (default: bronze)
 * @returns {Promise<Object>} - API response with transaction details
 */
export const searchTitleByProperty = async (searchParams, productType = 'current_owner_bronze') => {
  const credentials = getCredentials();

  if (!credentials.clientId || !credentials.clientSecret) {
    throw new Error('DataTrace credentials not configured. Please set credentials first.');
  }

  console.log('Starting DataTrace TIQSA title search:', searchParams);

  // Build the request payload for TIQSA API
  const requestBody = {
    product: {
      product_code: 'tiqsa',
      product_type: productType
    },
    property: {
      street_address: searchParams.address,
      city: searchParams.city,
      state: searchParams.state,
      postal_code: searchParams.zip,
      county: searchParams.county
    },
    // TIQSA requires parties field
    parties: [
      {
        role: 'buyer',
        entity_type: 'person',
        first_name: 'Unknown',
        last_name: 'Buyer',
        // Party details can be updated later if needed
      }
    ],
    client_reference_number: `TIQSA-${Date.now()}`,
    client_order_number: `ORDER-${Date.now()}`
  };

  // Add APN if provided
  if (searchParams.apn) {
    requestBody.property.parcel_id = searchParams.apn;
  }

  let response;
  try {
    response = await fetch('/api/datatrace/tiqsa', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        credentials: {
          clientId: credentials.clientId,
          clientSecret: credentials.clientSecret,
          scope: credentials.scope || `${credentials.clientId}/.default`
        },
        requestBody
      }),
    });
  } catch (fetchError) {
    const errorDetails = [
      `Network Error: ${fetchError.message}`,
      '',
      'Make sure the proxy server is running',
      '',
      '--- Search Parameters ---',
      JSON.stringify(searchParams, null, 2)
    ].join('\n');
    throw new Error(errorDetails);
  }

  const responseText = await response.text();
  console.log('TIQSA API Response:', responseText);

  let data;
  try {
    data = JSON.parse(responseText);
  } catch (parseError) {
    const errorDetails = [
      `JSON Parse Error: ${parseError.message}`,
      '',
      '--- Raw Response ---',
      responseText
    ].join('\n');
    throw new Error(errorDetails);
  }

  // Check for error response
  if (!response.ok || data.error) {
    const errorDetails = [
      `Error: ${data.error || `HTTP ${response.status}`}`,
      '',
      data.httpStatus ? `--- HTTP Status ---\n${data.httpStatus} ${data.httpStatusText || ''}` : '',
      '',
      '--- Details ---',
      data.details || '(no details)',
      '',
      data.errors ? '--- API Errors ---\n' + JSON.stringify(data.errors, null, 2) : '',
      '',
      '--- Request Sent ---',
      JSON.stringify(requestBody, null, 2)
    ].filter(Boolean).join('\n');
    throw new Error(errorDetails);
  }

  // Return the response - TIQSA is async, so it will have transaction_id
  return {
    transactionId: data.data?.transaction_id,
    confirmationNumber: data.data?.confirmation_number || data.data?.servicing_platform_confirmation_number,
    status: data.data?.report_status || data.data?.status || 'PENDING',
    productCode: 'tiqsa',
    productType: productType,
    requestBody,
    rawResponse: data.data,
    timestamp: new Date().toISOString()
  };
};

/**
 * Check the status of a title search transaction
 * Uses the generic checkTransactionStatus from datatraceTransactions
 * @param {string} transactionId - Transaction ID from initial search
 * @returns {Promise<Object>} - Status response
 */
export const checkTitleStatus = async (transactionId) => {
  const { checkTransactionStatus } = await import('./datatraceTransactions');
  return checkTransactionStatus(transactionId);
};

/**
 * Get the completed title report
 * Uses the generic getTransactionReport from datatraceTransactions
 * @param {string} transactionId - Transaction ID
 * @param {string} productCode - Product code (default: 'tiqsa')
 * @returns {Promise<Object>} - Report data
 */
export const getReport = async (transactionId, productCode = 'tiqsa') => {
  const { getTransactionReport } = await import('./datatraceTransactions');
  return getTransactionReport(transactionId, productCode);
};

export default {
  searchTitleByProperty,
  checkTitleStatus,
  getReport
};
